import { defineComponent, h, onMounted, ref, resolveComponent, computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import {
  CBadge,
  CSidebarNav,
  CNavItem,
  CNavGroup,
  CNavTitle,
} from '@coreui/vue'
import nav from './_nav.js'
import { useUserStore } from '@/plugins/store';

const itemMatchesSearch = (item, query) => {
  if (!query || query.trim() === '') {
    return true; // Muestra todo si no hay búsqueda
  }
  const lowerCaseQuery = query.toLowerCase();

  // Comprueba si el nombre del ítem principal coincide
  if (item.name && item.name.toLowerCase().includes(lowerCaseQuery)) {
    return true;
  }

  // Comprueba si algún sub-ítem coincide (para grupos)
  if (item.items) {
    return item.items.some(child => itemMatchesSearch(child, query));
  }

  return false;
}

const normalizePath = (path) =>
  decodeURI(path)
    .replace(/#.*$/, '')
    .replace(/(index)?\.(html)$/, '')

const isActiveLink = (route, link) => {
  
  if (link === undefined) {
    return false
  }

  if (route.hash === link) {
    return true
  }

  const currentPath = normalizePath(route.path)
  const targetPath = normalizePath(link)

  return currentPath === targetPath
}

const isActiveItem = (route, item) => {
  
  try{
    if (isActiveLink(route, item.to)) {
      return true
    }
  
    if (item.items) {
      return item.items.some((child) => isActiveItem(route, child))
    }
  }catch(e){
    console.log("detecte el error")
    location.reload();
  }

  return false
}

const AppSidebarNav = defineComponent({
  name: 'AppSidebarNav',
  components: {
    CNavItem,
    CNavGroup,
    CNavTitle,
  },
  // Define la prop para recibir el texto de búsqueda
  props: {
    searchQuery: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const route = useRoute()
    const firstRender = ref(true)
    
    const filteredNav = computed(() => {
      const userStore = useUserStore();
      const user = userStore.getUser;

      // Función recursiva de filtrado que también respeta los permisos
      const filterItems = (items) => {
        return items.reduce((acc, item) => {
          const query = props.searchQuery;

          // 1. Manejo de Permisos (tu lógica existente)
          const permission = user?.permissions?.find(p => p.to === item.to);
          const isAllowed = !permission || permission.allowed;

          if (!isAllowed) {
            // Si no tiene permiso, lo ignora
            return acc;
          }

          // 2. Manejo de Sub-ítems (grupos)
          let itemToAdd = { ...item };
          if (item.items) {
            // Filtra recursivamente los sub-ítems
            itemToAdd.items = filterItems(item.items);
            // Si el grupo tiene sub-ítems filtrados O si el nombre del grupo coincide
            if (itemToAdd.items.length > 0 || itemMatchesSearch(item, query)) {
              acc.push(itemToAdd);
            }
          } else if (item.to) {
            // 3. Manejo de Ítems Individuales (navitem)
            if (itemMatchesSearch(item, query)) {
              acc.push(itemToAdd);
            }
          } else {
             // 4. Manejo de Títulos (NavTitle) y otros ítems no navegables
             if (itemMatchesSearch(item, query) || item.component === 'CNavTitle') {
                acc.push(itemToAdd);
             }
          }

          return acc;
        }, []);
      };

      return filterItems(nav); // Asume que `nav` es tu arreglo de menú original importado.
    });
    onMounted(() => {
      firstRender.value = false
    })

    const renderItem = (item) => {
      if (item.items) {
        return h(
          CNavGroup,
          {
            ...(firstRender.value && {
              visible: item.items.some((child) => isActiveItem(route, child)),
            }),
          },
          {
            togglerContent: () => [
              h(resolveComponent('CIcon'), {
                customClassName: 'nav-icon',
                name: item.icon,
              }),
              item.name,
            ],
            default: () => item.items.map((child) => renderItem(child)),
          },
        )
      }

      return item.to
        ? h(
            RouterLink,
            {
              to: item.to,
              custom: true,
            },
            {
              default: (props) =>
                h(
                  resolveComponent(item.component),
                  {
                    active: props.isActive,
                    href: props.href,
                    onClick: () => props.navigate(),
                  },
                  {
                    default: () => [
                      item.icon &&
                        h(resolveComponent('CIcon'), {
                          customClassName: 'nav-icon',
                          name: item.icon,
                        }),
                      item.name,
                      item.badge &&
                        h(
                          CBadge,
                          {
                            class: 'ms-auto',
                            color: item.badge.color,
                          },
                          {
                            default: () => item.badge.text,
                          },
                        ),
                    ],
                  },
                ),
            },
          )
        : h(
            resolveComponent(item.component),
            {},
            {
              default: () => item.name,
            },
          )
    }

    return () =>
      h(
        CSidebarNav,
        {},
        {
          default: () => {
            // **Usa el menú filtrado en lugar del original**
            return filteredNav.value.map((item) => renderItem(item));
          },
        },
      )
  },
})

export { AppSidebarNav }
